/**
 * Logique PURE du canevas d'entretien : validation de schéma, score,
 * éliminatoires, recommandation. Aucun React, aucun réseau.
 *
 * ⚠️ C'est le SEUL module qui importe `canevas-entretien.ts` (le contenu, que
 * l'utilisateur remplace). Tout le reste du code passe par `CANEVAS_ACTIF`, et
 * les fonctions ci-dessous prennent le canevas en paramètre : elles se testent
 * donc sur n'importe quel canevas, pas seulement sur le contenu provisoire.
 */
import { CANEVAS_ENTRETIEN } from './canevas-entretien'
import type { Canevas, Question, Recommandation, Reponses } from './canevas-types'

const TYPES_VALIDES = ['note', 'oui_non', 'texte']
/** Clé réservée dans le jsonb des réponses : la synthèse y est rangée. */
export const CLE_SYNTHESE = '_synthese'
export const SEUIL_RETENIR = 3.5

const estTexte = (v: unknown): v is string => typeof v === 'string' && v.trim() !== ''
const estObjet = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

/**
 * Contrôle le canevas À L'EXÉCUTION : le fichier de contenu est remplacé à la
 * main par l'utilisateur, TypeScript ne le verra pas si le remplaçant est
 * bancal (ids en double, type inventé…). Renvoie la liste des erreurs, en
 * français ; vide = valide.
 */
export function validerCanevas(c: unknown): string[] {
  const erreurs: string[] = []
  if (!estObjet(c)) return ["Le canevas n'est pas un objet."]

  const v = c['version']
  if (!(typeof v === 'number' && Number.isFinite(v)) && !estTexte(v)) {
    erreurs.push('« version » est obligatoire (nombre ou texte).')
  }

  const sections = c['sections']
  if (!Array.isArray(sections) || sections.length === 0) {
    erreurs.push('« sections » doit être une liste non vide.')
  } else {
    const idSections = new Set<string>()
    const idQuestions = new Set<string>()
    sections.forEach((s: unknown, i) => {
      const nom = `Section ${i + 1}`
      if (!estObjet(s)) {
        erreurs.push(`${nom} : doit être un objet.`)
        return
      }
      const sid = s['id']
      if (!estTexte(sid)) erreurs.push(`${nom} : « id » obligatoire.`)
      else if (idSections.has(sid)) erreurs.push(`${nom} : l'identifiant « ${sid} » est en double.`)
      else idSections.add(sid)
      if (!estTexte(s['titre'])) erreurs.push(`${nom} : « titre » obligatoire.`)
      if (typeof s['consigne'] !== 'string') erreurs.push(`${nom} : « consigne » doit être un texte.`)
      const qs = s['questions']
      if (!Array.isArray(qs) || qs.length === 0) {
        erreurs.push(`${nom} : « questions » doit être une liste non vide.`)
        return
      }
      qs.forEach((q: unknown, j) => {
        const nq = `${nom}, question ${j + 1}`
        if (!estObjet(q)) {
          erreurs.push(`${nq} : doit être un objet.`)
          return
        }
        const qid = q['id']
        if (!estTexte(qid)) erreurs.push(`${nq} : « id » obligatoire.`)
        else if (qid === CLE_SYNTHESE) erreurs.push(`${nq} : l'identifiant « ${CLE_SYNTHESE} » est réservé.`)
        else if (idQuestions.has(qid)) erreurs.push(`${nq} : l'identifiant « ${qid} » est en double.`)
        else idQuestions.add(qid)
        if (!estTexte(q['texte'])) erreurs.push(`${nq} : « texte » obligatoire.`)
        const type = q['type']
        if (typeof type !== 'string' || !TYPES_VALIDES.includes(type)) {
          erreurs.push(`${nq} : type « ${String(type)} » invalide (attendu : note, oui_non ou texte).`)
        }
        if (q['aide'] !== undefined && typeof q['aide'] !== 'string') {
          erreurs.push(`${nq} : « aide » doit être un texte.`)
        }
        const elim = q['eliminatoire']
        if (elim !== undefined && typeof elim !== 'boolean') {
          erreurs.push(`${nq} : « eliminatoire » doit être vrai ou faux.`)
        } else if (elim === true && type === 'texte') {
          erreurs.push(`${nq} : une question de type texte ne peut pas être éliminatoire.`)
        }
      })
    })
  }

  const synthese = c['synthese']
  if (!estObjet(synthese) || !Array.isArray(synthese['criteres'])) {
    erreurs.push('« synthese.criteres » doit être une liste.')
  } else {
    const ids = new Set<string>()
    synthese['criteres'].forEach((k: unknown, i) => {
      const nc = `Critère de synthèse ${i + 1}`
      if (!estObjet(k)) {
        erreurs.push(`${nc} : doit être un objet.`)
        return
      }
      const kid = k['id']
      if (!estTexte(kid)) erreurs.push(`${nc} : « id » obligatoire.`)
      else if (ids.has(kid)) erreurs.push(`${nc} : l'identifiant « ${kid} » est en double.`)
      else ids.add(kid)
      if (!estTexte(k['libelle'])) erreurs.push(`${nc} : « libelle » obligatoire.`)
    })
  }
  return erreurs
}

/** Le canevas servi par l'application, et les erreurs de son schéma (vide = prêt). */
export const CANEVAS_ACTIF: Canevas = CANEVAS_ENTRETIEN
export const ERREURS_CANEVAS: string[] = validerCanevas(CANEVAS_ENTRETIEN)

export function toutesLesQuestions(canevas: Canevas): Question[] {
  return canevas.sections.flatMap((s) => s.questions)
}

/** Une note valide : un nombre de 1 à 5. Tout le reste compte pour « non renseigné ». */
const noteValide = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v) && v >= 1 && v <= 5

/**
 * Moyenne des notes 1-5 des questions de type `note` effectivement renseignées,
 * arrondie à 2 décimales. `oui_non` et `texte` ne comptent pas. Aucune note
 * renseignée : 0 (la colonne `score` de la base est `not null`).
 */
export function calculerScore(canevas: Canevas, reponses: Reponses): number {
  const notes: number[] = []
  for (const q of toutesLesQuestions(canevas)) {
    if (q.type !== 'note') continue
    const v = reponses[q.id]
    if (noteValide(v)) notes.push(v)
  }
  if (notes.length === 0) return 0
  const moyenne = notes.reduce((a, b) => a + b, 0) / notes.length
  return Math.round(moyenne * 100) / 100
}

/**
 * Questions éliminatoires DÉCLENCHÉES : `oui_non` répondu « non », ou `note`
 * renseignée ≤ 1. Une question laissée sans réponse ne déclenche rien.
 */
export function criteresEliminatoires(canevas: Canevas, reponses: Reponses): Question[] {
  return toutesLesQuestions(canevas).filter((q) => {
    if (!q.eliminatoire) return false
    const v = reponses[q.id]
    if (q.type === 'oui_non') return v === false
    if (q.type === 'note') return noteValide(v) && v <= 1
    return false
  })
}

/**
 * Recommandation proposée : éliminatoire → refuser ; score ≥ 3,5 → retenir ;
 * sinon revoir. Le staff peut en changer, SAUF « retenir » en présence d'un
 * éliminatoire (la base le refuse aussi : contrainte `entretien_elimine_pas_retenu`).
 */
export function recommandationSuggeree(score: number, elimine: boolean): Recommandation {
  if (elimine) return 'refuser'
  return score >= SEUIL_RETENIR ? 'retenir' : 'revoir'
}

export function recommandationAutorisee(reco: Recommandation, elimine: boolean): boolean {
  return !(elimine && reco === 'retenir')
}

/** Questions (hors texte libre) laissées sans réponse — confort : on prévient, on ne bloque pas. */
export function questionsSansReponse(canevas: Canevas, reponses: Reponses): Question[] {
  return toutesLesQuestions(canevas).filter((q) => {
    if (q.type === 'texte') return false
    const v = reponses[q.id]
    return q.type === 'oui_non' ? typeof v !== 'boolean' : !noteValide(v)
  })
}

export interface SyntheseEntretien {
  /** Note 1-5 par critère de synthèse (`synthese.criteres[].id`). */
  criteres: Record<string, number>
  notes: string
}

/**
 * L'objet envoyé dans la colonne jsonb `reponses` : les réponses par id de
 * question, plus la synthèse sous la clé réservée `_synthese`. Les réponses
 * vides (undefined, texte vide) sont omises.
 */
export function reponsesVersJson(
  canevas: Canevas,
  reponses: Reponses,
  synthese: SyntheseEntretien,
  recommandation: Recommandation,
): Record<string, unknown> {
  const sortie: Record<string, unknown> = {}
  for (const q of toutesLesQuestions(canevas)) {
    const v = reponses[q.id]
    if (v === undefined || (typeof v === 'string' && v.trim() === '')) continue
    sortie[q.id] = v
  }
  const criteres: Record<string, number> = {}
  for (const k of canevas.synthese.criteres) {
    const n = synthese.criteres[k.id]
    if (noteValide(n)) criteres[k.id] = n
  }
  sortie[CLE_SYNTHESE] = {
    criteres,
    recommandation,
    ...(synthese.notes.trim() ? { notes: synthese.notes.trim() } : {}),
  }
  return sortie
}
